import * as uuid from "uuid";
import CryptoES from "crypto-es";

import { ApiClient } from "./ApiClient";
import { Environment, getConfiguration } from "../../config";
import { User } from "../../models";

export class CognitoAuthApi extends ApiClient {
	private readonly cognitoAuthProvideName: string;
	private readonly user: User;

	constructor(env?: Environment) {
		const config = getConfiguration(env);

		super({
			baseApiUrl: config.cognitoAuthApiUrl,
			apiVersion: config.cognitoAuthApiVersion,
		});

		this.cognitoAuthProvideName = config.cognitoAuthProviderName;
		this.user = new User();
	}

	async appendHeaders(): Promise<HeadersInit> {
		return Promise.resolve({});
	}

	private computeDecryptionKey(username: string, pwd: string): string {
		const l = document.createElement("a");
		l.href = this.serviceBase;
		const host = l.hostname;
		const salt = username + this.cognitoAuthProvideName + host;
		const hashedSalt = CryptoES.HmacSHA256(salt, pwd);

		return hashedSalt.toString(CryptoES.enc.Hex);
	}

	private static byteArrayToWordArray(ba: number[]): any {
		const wa: number[] = [];
		for (let i = 0; i < ba.length; i++) {
			wa[(i / 4) | 0] |= ba[i] << (24 - 8 * i);
		}

		return CryptoES.lib.WordArray.create(wa, ba.length);
	}

	private static decrypt(data: string, key?: string): any {
		const byteCharacters = atob(data);
		const iv = new Array(16);
		const cryptText = new Array(byteCharacters.length - 16);

		for (let i = 0; i < byteCharacters.length; i++) {
			if (i < 16) {
				iv[i] = byteCharacters.charCodeAt(i);
			} else {
				cryptText[i - 16] = byteCharacters.charCodeAt(i);
			}
		}

		const cipherParams = CryptoES.lib.CipherParams.create({ ciphertext: CognitoAuthApi.byteArrayToWordArray(cryptText) });
		const plaintextArray = CryptoES.AES.decrypt(
			cipherParams,
			CryptoES.enc.Hex.parse(key),
			{ iv: CognitoAuthApi.byteArrayToWordArray(iv), mode: CryptoES.mode.CBC },
		);

		return JSON.parse(CryptoES.enc.Latin1.stringify(plaintextArray));
	}

	private getUid(): string {
		const uid = this.user.getCognitoInfo().uid || uuid.v1();
		this.user.updateCognitoInfo({ uid });

		return uid;
	}

	public async getToken(uid: string, _username: string, identityId: string | null): Promise<string> {
		const username = _username.toLowerCase();
		const { key } = this.user.getCognitoInfo();
		const timestamp = new Date().toISOString();
		const stringToSign = `${timestamp}${this.cognitoAuthProvideName}${username}${identityId ? identityId : ""}`;
		const signature = CryptoES.HmacSHA256(stringToSign, key);

		const query = {
			"provider1": this.cognitoAuthProvideName,
			"token1": encodeURIComponent(username),
			identityId,
			uid,
			timestamp,
			"signature": signature.toString(CryptoES.enc.Hex),
		};

		try {
			const res: string = await this.get("token", query) as unknown as string;
			const json = CognitoAuthApi.decrypt(res, key) as unknown as { identityId: string; token: string };
			this.user.updateCognitoInfo({ identityId: json.identityId });
			this.user.setUsername(username);

			return json.token;
		} catch (ignore) {
			window.parent.postMessage(JSON.stringify({ from: "WIDGETS", type: "AUTH_ISSUE" }), "*");
		}
	}

	public async login(_username: string, _pwd: string): Promise<boolean> {
		const username = _username.toLowerCase();
		const pwd = CryptoES.enc.Base64.stringify(CryptoES.enc.Utf8.parse(_pwd));
		const uid = this.getUid();
		const timestamp = new Date().toISOString();
		const signature = CryptoES.HmacSHA256(timestamp, this.computeDecryptionKey(username, pwd));

		const query = {
			uid,
			username: encodeURIComponent(username),
			timestamp,
			signature: signature.toString(CryptoES.enc.Hex),
		};

		return this.get("login", query)
			.then(res => {
				const key = this.computeDecryptionKey(username, pwd).substring(0, 32);
				const json = CognitoAuthApi.decrypt(res.encodedToB64 || res, key);

				this.user.updateCognitoInfo({ key: json.Key });

				return this.getToken(uid, username, null)
					.then(() => Promise.resolve(!!res.user2faEnabled));
			});
	}

	public async forgotPassword(username: string): Promise<string> {
		return this.post('/trigger/reset-password', {
			email: username.toLowerCase()
		})
	}

	public async confirmResetPassword(code: string): Promise<{email: string}> {
		const response = await this.get('/reset-password/' + code)
		if (!response?.email) {
			throw new Error("Invalid email")
		}

		return response
	}

	public async resetPassword(code: string, pwd: string):Promise<string> {
		return this.post('/reset-password', {
			code: code,
			password: CryptoES.enc.Base64.stringify(CryptoES.enc.Utf8.parse(pwd))
		})
	}


}
